import React, { useRef, useState } from "react";
import { Loader2 } from "lucide-react";

const CITATION_STYLES = ["APA", "MLA", "Chicago", "footnotes", "endnotes", "bibliography", "inline links"];

export default function SourcesAssistantPanel({
  selectedBlock,
  sources = [],
  claims = [],
  claimSummary,
  citationStyle,
  onCitationStyleChange,
  citationsText,
  busy,
  error,
  onSuggestSources,
  suggestScope,
  onSuggestScopeChange,
  onUploadResearch,
  attachScope,
  onAttachScopeChange,
  onReviewClaims,
  onSelectClaim,
  onCreateCitations,
  onAttachSourceToBlock,
}) {
  const fileRef = useRef(null);
  const [urlInput, setUrlInput] = useState("");
  const [notesInput, setNotesInput] = useState("");

  return (
    <section className="bww-right-section" data-testid="bl-sources-panel">
      <h3>Research and sources</h3>
      <p>
        {selectedBlock
          ? `Selected block: ${selectedBlock.type.replace(/_/g, " ")}`
          : "Attach research to this chapter or the whole book."}
      </p>
      {error && <div className="bww-panel-error" data-testid="bl-sources-error">{error}</div>}

      <div className="bww-style-grid">
        <label className="bww-style-field">
          <span>Suggest scope</span>
          <select data-testid="bl-suggest-scope" value={suggestScope} onChange={(e) => onSuggestScopeChange(e.target.value)}>
            <option value="selected_block">Selected block</option>
            <option value="selected_claim">Selected claim</option>
            <option value="chapter">Current chapter</option>
            <option value="book">Whole book</option>
          </select>
        </label>
        <label className="bww-style-field">
          <span>Attach upload to</span>
          <select data-testid="bl-attach-scope" value={attachScope} onChange={(e) => onAttachScopeChange(e.target.value)}>
            <option value="block">Selected block</option>
            <option value="chapter">Selected chapter</option>
            <option value="book">Entire book</option>
          </select>
        </label>
        <label className="bww-style-field">
          <span>Citation style</span>
          <select data-testid="bl-citation-style" value={citationStyle} onChange={(e) => onCitationStyleChange(e.target.value)}>
            {CITATION_STYLES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </div>

      <div className="bww-action-list" style={{ marginTop: 12 }}>
        <button type="button" className="bww-assistant-action" data-testid="bl-suggest-sources"
          disabled={busy} onClick={onSuggestSources}>
          {busy === "suggest" ? <><Loader2 className="animate-spin" size={12} /> Searching…</> : "Suggest trusted sources"}
        </button>
        <button type="button" className="bww-assistant-action" data-testid="bl-upload-research"
          disabled={busy} onClick={() => fileRef.current?.click()}>
          {busy === "upload-research" ? "Uploading…" : "Upload research document"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.docx,.txt,.md,text/plain,application/pdf"
          hidden
          data-testid="bl-research-file"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onUploadResearch({ file: f });
            e.target.value = "";
          }}
        />
        <div className="bww-style-grid">
          <label className="bww-style-field">
            <span>Or add URL</span>
            <input data-testid="bl-research-url" value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="https://…" />
          </label>
          <button type="button" className="bww-assistant-action" data-testid="bl-research-url-submit"
            disabled={busy || !urlInput.trim()}
            onClick={() => { onUploadResearch({ url: urlInput.trim() }); setUrlInput(""); }}>
            Attach URL
          </button>
          <label className="bww-style-field">
            <span>Or plain notes</span>
            <textarea data-testid="bl-research-notes" rows={3} value={notesInput} onChange={(e) => setNotesInput(e.target.value)} />
          </label>
          <button type="button" className="bww-assistant-action" data-testid="bl-research-notes-submit"
            disabled={busy || !notesInput.trim()}
            onClick={() => { onUploadResearch({ notes: notesInput.trim() }); setNotesInput(""); }}>
            Save notes as research
          </button>
        </div>
        <button type="button" className="bww-assistant-action" data-testid="bl-review-claims"
          disabled={busy} onClick={onReviewClaims}>
          {busy === "claims" ? "Reviewing…" : "Review factual claims"}
        </button>
        <button type="button" className="bww-assistant-action" data-testid="bl-create-citations"
          disabled={busy} onClick={onCreateCitations}>
          {busy === "citations" ? "Creating…" : "Create citation list"}
        </button>
      </div>

      {claimSummary && (
        <div className="bww-claim-summary" data-testid="bl-claim-summary">
          <strong>{claimSummary.total} claims found</strong>
          <span>{claimSummary.verified} verified</span>
          <span>{claimSummary.needs_review} need review</span>
          <span>{claimSummary.unsupported} unsupported</span>
        </div>
      )}

      {claims.length > 0 && (
        <div className="bww-claim-list" data-testid="bl-claim-list">
          {claims.map((c) => (
            <button
              key={c.id}
              type="button"
              className="bww-claim-item"
              data-testid={`bl-claim-${c.id}`}
              onClick={() => onSelectClaim(c)}
            >
              <span className={`bww-claim-status ${c.status}`}>{c.status.replace(/_/g, " ")}</span>
              <span>{c.text}</span>
            </button>
          ))}
        </div>
      )}

      {sources.length > 0 && (
        <div className="bww-source-list" data-testid="bl-source-list">
          <h4>Attached sources</h4>
          {sources.map((s) => (
            <div key={s.id} className="bww-source-item" data-testid={`bl-source-${s.id}`}>
              <strong>{s.title || s.filename || "Untitled source"}</strong>
              <span>{s.source_type || s.sourceType} · {s.verification_status || "unverified"}</span>
              {s.url && <a href={s.url} target="_blank" rel="noreferrer">{s.url}</a>}
              {s.processing_error && <em className="bww-panel-error">{s.processing_error}</em>}
              <button type="button" className="bww-btn" disabled={!selectedBlock}
                onClick={() => onAttachSourceToBlock(s.id)}>
                Attach to selected block
              </button>
            </div>
          ))}
        </div>
      )}

      {citationsText && (
        <pre className="bww-citations" data-testid="bl-citations-output">{citationsText}</pre>
      )}
    </section>
  );
}
